import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useMemo, useState } from "react";
import { OUTCOME_TEMPLATES } from "../../../shared/outcomeTemplates";
import { collectAllTags } from "../../../shared/stepOrchestration";
import { pathScreens } from "../../../shared/tour";
import type { DemoBundle, Outcome } from "../../../shared/types";
import { api } from "../../api";
import { IconLayers, IconPlus, IconTrash } from "../../icons";
import { kindLabel } from "../studio/StepList";
import { useEditor } from "../DemoEditor";
import TagAutocompleteInput from "./TagAutocompleteInput";

/**
 * Paths and how viewers are routed to them. Every path is its own list of screens (built in the Studio); here each
 * path gets its routing tags, and the list's order breaks ties. The rule itself is `routePath` in shared/tour.ts.
 */
export default function PathsTab() {
  const { bundle, setBundle, go } = useEditor();
  const [selected, setSelected] = useState<string | null>(null);
  const [order, setOrder] = useState(bundle.outcomes.map((o) => o.id));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setOrder(bundle.outcomes.map((o) => o.id)), [bundle.outcomes]);
  useEffect(() => { if (selected && !bundle.outcomes.some((o) => o.id === selected)) setSelected(null); }, [bundle.outcomes, selected]);
  const path = selected ? bundle.outcomes.find((o) => o.id === selected) : undefined;

  const act = async (fn: () => Promise<DemoBundle>) => {
    setError(null);
    try { setBundle(await fn()); return true; } catch (e: any) { setError(e.message); return false; }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(order, order.indexOf(String(e.active.id)), order.indexOf(String(e.over.id)));
    setOrder(next);
    if (!(await act(() => api.reorderPaths(bundle.demo.id, next)))) setOrder(bundle.outcomes.map((o) => o.id));
  };

  const addPath = async () => {
    const unused = OUTCOME_TEMPLATES.find((t) => !bundle.outcomes.some((o) => o.name === t.name));
    const name = prompt("Name of the new path (it starts with copies of the default path's screens):", unused?.name ?? "New path")?.trim();
    if (!name) return;
    setError(null);
    try {
      const template = OUTCOME_TEMPLATES.find((t) => t.name.toLowerCase() === name.toLowerCase());
      const r = await api.addPath(bundle.demo.id, { name, templateKey: template?.key ?? null });
      setBundle(r.bundle);
      setSelected(r.id);
    } catch (e: any) { setError(e.message); }
  };

  const byId = new Map(bundle.outcomes.map((o) => [o.id, o]));
  return (
    <div className="stack" style={{ maxWidth: 1120 }}>
      <div className="route-note" style={{ margin: 0 }}><span>
        Each viewer sees one path. Their survey answers give them tags, and they get the path whose routing tags they
        match the most of. A tie goes to the path higher in this list (drag to reorder); no match, or no survey, gets the
        default path. A link ending <code>?path=</code> and a path's name (or <code>default</code>) opens that path directly.
      </span></div>
      <div className="orch-wrap">
        <aside className="orch-timeline-col" aria-label="Paths">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: "var(--fs-m)" }}>Paths</h3>
            <span className="small muted">{bundle.outcomes.length + 1}</span>
          </div>
          <div className="orch-timeline">
            <PathCard name="Default path" sub={`${pathScreens(bundle, null).length} screens · for viewers no path matches`} active={selected === null} onSelect={() => setSelected(null)} />
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={order} strategy={verticalListSortingStrategy}>
                {order.map((id, i) => {
                  const o = byId.get(id);
                  if (!o) return null;
                  return (
                    <PathCard key={id} id={id} rank={i + 1} name={o.name} active={selected === id} onSelect={() => setSelected(id)}
                      sub={`${pathScreens(bundle, id).length} screens · ${o.tags.length ? o.tags.map((t) => `#${t}`).join(" ") : "no routing tags"}`} />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
          <button type="button" className="btn orch-add-btn" onClick={() => void addPath()}><IconPlus />New path</button>
        </aside>

        <section className="orch-config-col" aria-label="Path settings">
          {path ? <PathPanel key={path.id} path={path} act={act} /> : <DefaultPanel />}
        </section>
      </div>
      {error && <div className="error-box">{error}</div>}
      <div className="tab-foot">
        <button type="button" className="btn btn-ghost" onClick={() => go("basics")}>Back to Settings</button>
        <span className="spacer" />
        <button type="button" className="btn btn-primary" onClick={() => go("studio", selected ? `?path=${selected}` : "")}>Continue to the Studio</button>
      </div>
    </div>
  );
}

function PathCard({ id, rank, name, sub, active, onSelect }: { id?: string; rank?: number; name: string; sub: string; active: boolean; onSelect: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: id ?? "__default", disabled: !id });
  return (
    <div ref={setNodeRef} className={`orch-card ${active ? "active" : ""}`} onClick={onSelect} aria-current={active ? "true" : undefined}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}>
      <div className="orch-node" aria-hidden />
      {id ? (
        <button type="button" className="orch-card-grip" aria-label={`Reorder ${name}`} title="Drag to reorder: higher wins a tie" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden><circle cx="6" cy="4" r="1.1" /><circle cx="10" cy="4" r="1.1" /><circle cx="6" cy="8" r="1.1" /><circle cx="10" cy="8" r="1.1" /><circle cx="6" cy="12" r="1.1" /><circle cx="10" cy="12" r="1.1" /></svg>
        </button>
      ) : <span className="orch-card-grip" aria-hidden><IconLayers className="icon icon-xs" /></span>}
      <div className="orch-card-body">
        <span className="orch-card-title">{rank ? `${rank}. ` : ""}{name}</span>
        <span className="orch-card-sub">{sub}</span>
      </div>
    </div>
  );
}

/** The screens of a path, read-only: they are added, removed and ordered in the Studio. */
function ScreenList({ pathId }: { pathId: string | null }) {
  const { bundle, go } = useEditor();
  const seq = pathScreens(bundle, pathId);
  return (
    <div className="stack-s">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "var(--fs-s)", fontWeight: 600 }}>Screens ({seq.length})</span>
        <button type="button" className="btn btn-sm" onClick={() => go("studio", pathId ? `?path=${pathId}` : "")}>Edit in the Studio</button>
      </div>
      {seq.length === 0 ? <p className="small muted" style={{ margin: 0 }}>No screens yet. Add some in the Studio.</p> : (
        <ol className="path-screens">
          {seq.map((s) => (
            <li key={s.id}>
              <span>{s.caption || s.title || kindLabel(s)}</span>
              <span className="small muted">{kindLabel(s)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DefaultPanel() {
  return (
    <div className="orch-panel">
      <div className="orch-panel-header"><div><span className="small muted" style={{ display: "block", marginBottom: 2 }}>The fallback</span><h2 className="orch-panel-title">Default path</h2></div></div>
      <p className="small muted" style={{ margin: 0 }}>
        Viewers whose tags match no path, viewers who skip the survey, and every viewer when there is no survey, see the
        default path. It has no routing tags and can't be deleted.
      </p>
      <ScreenList pathId={null} />
    </div>
  );
}

function PathPanel({ path, act }: { path: Outcome; act: (fn: () => Promise<DemoBundle>) => Promise<boolean> }) {
  const { bundle } = useEditor();
  const [name, setName] = useState(path.name);
  const suggestions = useMemo(() => collectAllTags(bundle.questions, bundle.outcomes), [bundle.questions, bundle.outcomes]);
  // Which survey answers give each tag, so it's plain which viewers a tag will route here.
  const givenBy = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const q of bundle.questions) for (const o of q.options) for (const t of o.attached_tags ?? []) {
      const k = t.trim();
      if (k) m.set(k, [...(m.get(k) ?? []), `“${o.label}”`]);
    }
    return m;
  }, [bundle.questions]);
  const setTags = (tags: string[]) => act(() => api.updatePath(bundle.demo.id, path.id, { tags }));
  const saveName = () => {
    const n = name.trim();
    if (!n) { setName(path.name); return; }
    if (n !== path.name) void act(() => api.updatePath(bundle.demo.id, path.id, { name: n }));
  };
  const remove = () => {
    const n = pathScreens(bundle, path.id).length;
    if (confirm(`Delete the path "${path.name}" and its ${n} ${n === 1 ? "screen" : "screens"}? Other paths keep their own. Viewers it would have routed get the default path.`)) void act(() => api.deletePath(bundle.demo.id, path.id));
  };

  return (
    <div className="orch-panel">
      <div className="orch-panel-header">
        <div><span className="small muted" style={{ display: "block", marginBottom: 2 }}>Path {bundle.outcomes.findIndex((o) => o.id === path.id) + 1} of {bundle.outcomes.length}</span><h2 className="orch-panel-title">{path.name}</h2></div>
        <div className="orch-header-actions"><button type="button" className="btn btn-sm btn-danger" onClick={remove}><IconTrash />Delete path</button></div>
      </div>
      <label className="field">
        <span>Name <span className="hint">Also its link: ?path={path.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}</span></span>
        <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
      </label>
      <div className="orch-tag-box">
        <span style={{ fontSize: "var(--fs-s)", fontWeight: 600 }}>Routing tags</span>
        <p className="small muted" style={{ margin: 0 }}>A viewer is routed here when this path matches more of their tags than any other path. Tags come from the answers in the Survey.</p>
        <div className="orch-tags-list">
          {path.tags.length === 0 && <span className="small muted" style={{ fontStyle: "italic" }}>No tags yet: the survey never routes anyone here.</span>}
          {path.tags.map((tag) => (
            <span key={tag} className="orch-tag-pill" title={givenBy.has(tag) ? `Given by ${givenBy.get(tag)!.join(", ")}` : "No survey answer gives this tag yet"}>
              #{tag}{!givenBy.has(tag) && " ⚠"}
              <button type="button" className="orch-tag-del" aria-label={`Remove tag ${tag}`} onClick={() => void setTags(path.tags.filter((t) => t !== tag))}>×</button>
            </span>
          ))}
        </div>
        <TagAutocompleteInput activeTags={path.tags} allSuggestions={suggestions} onAddTag={(t) => { if (!path.tags.includes(t)) void setTags([...path.tags, t]); }}
          placeholder="Add a tag (e.g. cost-reduction) — press Enter" />
        {path.tags.some((t) => givenBy.has(t)) && (
          <ul className="small muted path-tag-sources">
            {path.tags.filter((t) => givenBy.has(t)).map((t) => <li key={t}>#{t}: {givenBy.get(t)!.join(", ")}</li>)}
          </ul>
        )}
      </div>
      <ScreenList pathId={path.id} />
    </div>
  );
}
